import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NightPicker } from "@/components/NightPicker";
import { TicketPicker } from "@/components/TicketPicker";
import { PartyStepper, OrderSummary } from "@/components/PartyStepper";
import { shortDate } from "@/lib/api";

const Step = ({ n, title, children }) => (
  <div className="space-y-4">
    <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#D9B864]">{n} · {title}</p>
    {children}
  </div>
);

const ComingSoon = ({ ev }) => (
  <div className="space-y-5" data-testid="coming-soon-panel">
    <p className="inline-block rounded-full border border-dashed border-[#C9A24B] px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[#D9B864]">Tickets coming soon</p>
    <ul className="divide-y divide-white/10 rounded-md border border-white/15">
      {ev.nights.map((n) => {
        const d = shortDate(n.date);
        return (
          <li key={n.id} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`coming-soon-night-${n.id}`}>
            <span className="font-display text-lg text-white">{d.weekday}, {d.month} {d.day}, {d.year}</span>
            <span className="text-right text-sm text-[#BDB3A5]">{[n.city, n.venue].filter(Boolean).join(" · ")}</span>
          </li>
        );
      })}
    </ul>
    <p className="text-sm text-[#BDB3A5]">Dates and locations may change. Call <a href="tel:+15027830430" className="text-[#D9B864]">(502) 783-0430</a> to hold seats early.</p>
  </div>
);

export const BookingPanel = ({ ev, initialNight }) => {
  const navigate = useNavigate();
  const firstOpen = ev.nights.find((n) => !n.sold_out);
  const [nightId, setNightId] = useState(ev.nights.find((n) => n.id === initialNight && !n.sold_out)?.id || firstOpen?.id);
  const [ticketId, setTicketId] = useState(ev.ticket_types[0]?.id);
  const [qty, setQty] = useState(1);
  const night = ev.nights.find((n) => n.id === nightId);
  const ticket = ev.ticket_types.find((t) => t.id === ticketId);
  const max = Math.min(20, night?.remaining || 1);

  if (ev.coming_soon) return <ComingSoon ev={ev} />;

  if (!ev.nights.length || !ev.ticket_types.length) {
    return <p className="text-[#BDB3A5]" data-testid="no-nights-message">New dates will be announced soon.</p>;
  }

  const reserve = () => navigate(`/checkout?event=${ev.slug}&night=${nightId}&ticket=${ticketId}&qty=${Math.min(qty, max)}`);

  return (
    <div className="space-y-10" data-testid="booking-panel">
      <Step n="1" title="Choose a night">
        <NightPicker nights={ev.nights} value={nightId} onChange={(id) => { setNightId(id); }} />
        {(night?.venue || night?.city) && <p className="text-sm text-white" data-testid="night-location">{[night.venue, night.city].filter(Boolean).join(" · ")}</p>}
        {night?.description && <p className="text-sm text-[#BDB3A5]" data-testid="night-description">{night.label}: {night.description}</p>}
      </Step>
      <Step n="2" title="Choose your seat"><TicketPicker tickets={ev.ticket_types} value={ticketId} onChange={setTicketId} /></Step>
      <Step n="3" title="Your party">
        <PartyStepper value={Math.min(qty, max)} onChange={setQty} max={max} />
        <OrderSummary ticket={ticket} qty={Math.min(qty, max)} fee={ev.service_fee} />
      </Step>
      <button type="button" onClick={reserve} disabled={!night || !ticket} data-testid="reserve-button"
        className="w-full rounded border border-[#C9A24B] py-4 text-sm font-semibold uppercase tracking-[0.2em] text-[#F3DDA4] transition-colors duration-200 hover:bg-[#C9A24B] hover:text-[#141210] disabled:opacity-40">
        Reserve
      </button>
    </div>
  );
};
